import type { TicketViewPreferences } from '@/types/ui';

/**
 * Utility functions for managing ticket view preferences in localStorage
 * Provides persistent storage for user's preferred ticket list view settings
 */

const STORAGE_KEY = 'supportdesk_ticket_view_preferences';

// Default preferences
export const defaultTicketViewPreferences: TicketViewPreferences = {
  viewType: 'table',
  tableColumns: ['select', 'id', 'subject', 'requester', 'status', 'priority', 'assignedTo', 'updatedAt', 'actions'],
  cardSize: 'comfortable',
  tableRowHeight: 'comfortable',
  showAvatars: true,
  showPreviews: true
};

/**
 * Load ticket view preferences from localStorage
 */
export const loadTicketViewPreferences = (): TicketViewPreferences => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return defaultTicketViewPreferences;
    }

    const parsed = JSON.parse(stored);
    
    // Validate the structure and merge with defaults for any missing properties
    return {
      ...defaultTicketViewPreferences,
      ...parsed,
      // Ensure viewType is valid
      viewType: ['table', 'card', 'inbox'].includes(parsed.viewType) 
        ? parsed.viewType 
        : defaultTicketViewPreferences.viewType,
      // Ensure tableColumns is an array and Actions column is always last
      tableColumns: Array.isArray(parsed.tableColumns)
        ? validateTableColumns(parsed.tableColumns)
        : defaultTicketViewPreferences.tableColumns,
      // Ensure cardSize is valid
      cardSize: ['compact', 'comfortable'].includes(parsed.cardSize)
        ? parsed.cardSize
        : defaultTicketViewPreferences.cardSize,
      // Ensure tableRowHeight is valid
      tableRowHeight: ['compact', 'comfortable'].includes(parsed.tableRowHeight)
        ? parsed.tableRowHeight
        : defaultTicketViewPreferences.tableRowHeight,
      // Ensure booleans
      showAvatars: typeof parsed.showAvatars === 'boolean' 
        ? parsed.showAvatars 
        : defaultTicketViewPreferences.showAvatars,
      showPreviews: typeof parsed.showPreviews === 'boolean'
        ? parsed.showPreviews
        : defaultTicketViewPreferences.showPreviews
    };
  } catch (error) {
    console.warn('Failed to load ticket view preferences from localStorage:', error);
    return defaultTicketViewPreferences;
  }
};

/**
 * Save ticket view preferences to localStorage
 */
export const saveTicketViewPreferences = (preferences: TicketViewPreferences): void => {
  try {
    // Ensure Actions column is always at the end before saving
    const correctedPreferences = {
      ...preferences,
      tableColumns: validateTableColumns(preferences.tableColumns)
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(correctedPreferences));
  } catch (error) {
    console.warn('Failed to save ticket view preferences to localStorage:', error);
  }
};

/**
 * Reset ticket view preferences to defaults
 */
export const resetTicketViewPreferences = (): TicketViewPreferences => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to reset ticket view preferences:', error);
  }
  return defaultTicketViewPreferences;
};

/**
 * React hook for managing ticket view preferences with localStorage persistence
 */
export const useTicketViewPreferences = () => {
  const [preferences, setPreferencesState] = React.useState<TicketViewPreferences>(
    loadTicketViewPreferences
  );

  const setPreferences = React.useCallback((newPreferences: TicketViewPreferences) => {
    setPreferencesState(newPreferences);
    saveTicketViewPreferences(newPreferences);
  }, []);

  const updatePreferences = React.useCallback((
    updates: Partial<TicketViewPreferences>
  ) => {
    let newPreferences = { ...preferences, ...updates };

    // If tableColumns is being updated, ensure Actions column is at the end
    if (updates.tableColumns) {
      newPreferences.tableColumns = validateTableColumns(updates.tableColumns);
    }

    setPreferences(newPreferences);
  }, [preferences, setPreferences]);

  const resetPreferences = React.useCallback(() => {
    const defaults = resetTicketViewPreferences();
    setPreferencesState(defaults);
  }, []);

  return {
    preferences,
    setPreferences,
    updatePreferences,
    resetPreferences
  };
};

/**
 * Utility to validate column configuration for table view
 */
export const validateTableColumns = (columns: string[]): string[] => {
  const validColumns = [
    'select', 'id', 'subject', 'requester', 'company', 'status',
    'priority', 'assignedTo', 'createdAt', 'updatedAt', 'actions'
  ];
  
  const requiredColumns = ['select', 'actions'];
  
  // Filter out invalid columns and ensure required columns are present
  const validatedColumns = columns.filter(col => validColumns.includes(col));
  
  // Add required columns if missing
  requiredColumns.forEach(col => {
    if (!validatedColumns.includes(col)) {
      validatedColumns.push(col);
    }
  });
  
  // Ensure select column is first and actions column is last
  const filteredColumns = validatedColumns.filter(col => 
    !requiredColumns.includes(col)
  );
  
  return ['select', ...filteredColumns, 'actions'];
};

/**
 * Generate column configuration with proper ordering
 */
export const generateColumnConfig = (
  selectedColumns: string[], 
  availableColumns: Array<{ key: string; label: string; required?: boolean }>
): string[] => {
  const orderedColumns: string[] = [];
  
  // Always start with select if it exists
  if (selectedColumns.includes('select')) {
    orderedColumns.push('select');
  }
  
  // Add other columns in the order they appear in availableColumns
  availableColumns.forEach(col => {
    if (selectedColumns.includes(col.key) && col.key !== 'select' && col.key !== 'actions') {
      orderedColumns.push(col.key);
    }
  });
  
  // Always end with actions if it exists
  if (selectedColumns.includes('actions')) {
    orderedColumns.push('actions');
  }
  
  return orderedColumns;
};

/**
 * Get responsive card configuration based on screen size
 */
export const getResponsiveCardConfig = (screenWidth: number) => {
  if (screenWidth < 640) {
    // Mobile: Single column, compact cards
    return {
      gridCols: 'grid-cols-1',
      cardSize: 'compact' as const,
      showQuickActions: false
    };
  } else if (screenWidth < 1024) {
    // Tablet: 2 columns
    return {
      gridCols: 'grid-cols-1 sm:grid-cols-2',
      cardSize: 'comfortable' as const,
      showQuickActions: true
    };
  } else if (screenWidth < 1280) {
    // Desktop: 3 columns
    return {
      gridCols: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      cardSize: 'comfortable' as const,
      showQuickActions: true
    };
  } else {
    // Large desktop: 4 columns
    return {
      gridCols: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
      cardSize: 'comfortable' as const,
      showQuickActions: true
    };
  }
};

// Re-export React for the hook
import React from 'react';